import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatBudgetDays } from "@/lib/employeeAbsences";
import { formatHours, getEmployeeAccountSummary } from "@/lib/employeeTimes";

interface PersonalAccountSummaryProps {
  employeeLogin: string;
  refreshKey?: number;
}

export function PersonalAccountSummary({
  employeeLogin,
  refreshKey = 0,
}: PersonalAccountSummaryProps) {
  const year = new Date().getFullYear();
  const [overtimeHours, setOvertimeHours] = useState<number | null>(null);
  const [vacationRemaining, setVacationRemaining] = useState<number | null>(null);
  const [overtimeSource, setOvertimeSource] = useState<"app" | "hero" | null>(null);
  const [overtimeExcludesToday, setOvertimeExcludesToday] = useState(false);
  const [tracksOvertime, setTracksOvertime] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeLogin) {
      setOvertimeHours(null);
      setVacationRemaining(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    void getEmployeeAccountSummary({ user_login: employeeLogin, year })
      .then((summary) => {
        if (!alive) return;
        setTracksOvertime(summary.tracks_overtime !== false);
        setOvertimeHours(
          summary.tracks_overtime === false
            ? null
            : summary.overtime_balance_hours != null
              ? Number(summary.overtime_balance_hours)
              : null,
        );
        const available =
          summary.available_vacation_days != null
            ? Number(summary.available_vacation_days)
            : summary.remaining_vacation_days != null
              ? Number(summary.remaining_vacation_days)
              : null;
        setVacationRemaining(available);
        setOvertimeSource(summary.overtime_source ?? null);
        setOvertimeExcludesToday(Boolean(summary.overtime_excludes_today));
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err.message : "Kontodaten konnten nicht geladen werden.");
        setOvertimeHours(null);
        setVacationRemaining(null);
        setOvertimeSource(null);
        setOvertimeExcludesToday(false);
        setTracksOvertime(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [employeeLogin, year, refreshKey]);

  if (!employeeLogin) return null;

  if (loading) {
    return (
      <div className="flex h-14 items-center justify-end px-2">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  if (error && overtimeHours == null && vacationRemaining == null) {
    return (
      <div className="max-w-[220px] text-right text-xs text-label-secondary" title={error}>
        Kontodaten nicht verfügbar
      </div>
    );
  }

  const overtimeTitle =
    overtimeSource === "app"
      ? "Aktueller Überstundenstand (App, ohne heutigen Tag)"
      : "Aktueller Überstundenstand";
  const vacationTitle = `Noch verfügbarer Urlaub ${year} (App+Hero, inkl. offener Anträge)`;

  return (
    <div className="flex flex-wrap items-stretch justify-end gap-2 sm:gap-3">
      {tracksOvertime && (
        <div
          className="min-w-[7.5rem] rounded-ios border border-separator bg-card px-3 py-2 text-right shadow-ios"
          title={overtimeTitle}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-secondary">
            Stundenkonto
          </div>
          <div className="mt-0.5 text-lg font-bold tabular-nums text-label">
            {overtimeHours != null ? formatHours(overtimeHours) : "—"}
          </div>
          {overtimeExcludesToday && (
            <div className="mt-0.5 text-[10px] leading-tight text-label-tertiary">
              Heutiger Tag nicht berücksichtigt
            </div>
          )}
        </div>
      )}
      <div
        className="min-w-[7.5rem] rounded-ios border border-separator bg-card px-3 py-2 text-right shadow-ios"
        title={vacationTitle}
      >
        <div className="text-[10px] font-semibold uppercase tracking-wide text-label-secondary">
          Urlaub {year}
        </div>
        <div className="mt-0.5 text-lg font-bold tabular-nums text-label">
          {vacationRemaining != null ? formatBudgetDays(vacationRemaining) : "—"}
        </div>
        <div className="text-[10px] text-label-secondary">Tage rest</div>
      </div>
    </div>
  );
}
